import { Fragment, isValidElement, useEffect, useRef, useState, type ReactNode } from "react";
import { api, formatDate, formatMoney, formatPhone } from "../api";
import { ErrorState, EmptyState, LoadingState, StatusBadge } from "./Ui";
import Markdown from "./Markdown";
export type RecordData = Record<string, any>;
export const label = (s: string): string =>
  (
    ({
      lsa: "LSA",
      office_follow_up: "Called back",
      ctm: "CTM",
      reason: "Why",
      sop_rule: "Playbook rule",
      sop_warnings: "Playbook notes",
      target_review: "Target check",
      googleads: "Google Ads",
      linkedin: "LinkedIn",
      tiktok: "TikTok",
      youtube: "YouTube",
      snapshot_date: "Date",
      location_name: "Location",
      implied_roas: "ROAS",
      roas: "ROAS",
      phone_e164: "Phone",
      utm_source: "UTM source",
      has_gclid: "Click ID",
      calls_30d: "Calls (30 days)",
      calls_60s: "Calls 60s+",
      market_name: "Territory",
      location: "Territory",
      todo: "To do",
      booked_jobs: "Booked",
      completed_jobs: "Completed",
      cancelled_jobs: "Cancelled",
      cost_per_booked_job: "Cost per booked job",
      how_heard: "Customer said",
      lead_id: "LSA lead ID",
      submitted_at: "Submitted",
      full_name: "Name",
      campaign_name: "Campaign",
      tracking_number: "Number",
      last_call: "Last call",
      blended_roas: "Blended ROAS",
      credited_in_google: "Credited by Google",
      cost_per_credited_booking: "Cost per credited booking",
    }) as Record<string, string>
  )[s] || sentence(s);

// Acronyms keep their capitals when a key becomes a label.
const ACRONYMS: Record<string, string> = {
  ghl: "GHL", ctm: "CTM", cpa: "CPA", cpc: "CPC", ctr: "CTR", roas: "ROAS", gbp: "GBP", url: "URL", kb: "KB",
  ai: "AI", lsa: "LSA", sms: "SMS", seo: "SEO", id: "ID", ids: "IDs", utm: "UTM", crm: "CRM", sop: "SOP", sops: "SOPs",
  google: "Google", meta: "Meta",
  api: "API", ppc: "PPC", aov: "AOV", ltv: "LTV", ajs: "AJS", gclid: "GCLID", usp: "USP",
  // Product names keep their capital: "google_ads" reads "Google Ads", never "Google ads".
  ads: "Ads",
};
/** "scoring_and_coaching" → "Scoring and coaching"; "ghl_activity" → "GHL activity". */
export function sentence(key: string): string {
  const words = String(key).replaceAll("_", " ").trim().split(/\s+/).filter(Boolean);
  return words
    .map((w, i) => ACRONYMS[w.toLowerCase()] || (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w.toLowerCase()))
    .join(" ");
}
const MONEY_KEYS = [
  "spend", "ad_spend", "completed_revenue", "cost_per_call", "cost_per_booked", "cost_per_completed",
  "avg_job_size", "revenue", "cost_per_booked_job", "cost_per_credited_booking", "cpa", "cpc", "job_total", "avg_job",
];
/** A column of numbers (counts, money, ratios) lines up on the right so the eye can run down it. */
function isNumericColumn(key: string, rows: RecordData[]): boolean {
  if (MONEY_KEYS.includes(key) || /roas$|_rate$|_pct$|completion_pct$/.test(key)) return true;
  const sample = rows.find((r) => r[key] != null && r[key] !== "");
  return sample ? typeof sample[key] === "number" : false;
}
function Cell({ name, value }: { name: string; value: any }) {
  if (value == null || value === "") return <>—</>;
  // A caller-built cell (a link, a badge) renders as given.
  if (isValidElement(value)) return value;
  if (typeof value === "object") return <Value value={value} />;
  if (["platform", "idea_type", "rock_type", "content_type"].includes(name))
    return <>{label(String(value))}</>;
  if (name === "caller_number") return <>{formatPhone(String(value))}</>;
  // Campaign names like "JK|Sacramento|Search|Leads|V1" may wrap after a bar on a phone, so the
  // columns beside them stay on screen; nothing breaks mid-word.
  if (name === "campaign" && typeof value === "string" && value.includes("|"))
    return <>{value.split("|").map((part, i) => <Fragment key={i}>{i > 0 && <>|<wbr /></>}{part}</Fragment>)}</>;
  // Ticked once someone calls the caller back; LSA rows pass the day it was ticked.
  if (name === "office_follow_up") return <>{typeof value === "string" ? value : value ? "Yes" : "—"}</>;
  if (name === "status")
    return <StatusBadge>{label(String(value))}</StatusBadge>;
  if (/(_at|_date|^week_of$|^period_start$|^period_end$|^last_call$|^last_event$)$/.test(name))
    return <>{formatDate(String(value))}</>;
  // Seconds read as 12:34, not 754.
  if (/(^talk_time$|^duration$|_seconds$)/.test(name) && Number.isFinite(Number(value))) {
    const sec = Math.round(Number(value));
    return <>{`${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`}</>;
  }
  // Raw lowercase states from providers ("answered", "no answer") get a capital.
  if (/^(call_status|outcome|direction|priority|severity)$/.test(name) && typeof value === "string")
    return <>{value.charAt(0).toUpperCase() + value.slice(1).replaceAll("_", " ")}</>;
  // Call trackers send names in capitals ("R CHRISTIAN", "SACRAMENTO CA"); read them as names.
  if (/name$/.test(name) && typeof value === "string" && value.length > 2 && value === value.toUpperCase() && /[A-Z]/.test(value))
    return <>{value.toLowerCase().replace(/(^|[\s\-'])([a-z])/g, (m) => m.toUpperCase()).replace(/\b(Ca|Nv|Az|Or|Wa|Tx|Fl|Ny|Nj|Il|Mi|Ga|Sc|Nc|Ne|Ks|Pa|Ma|Co|Ut)$/, (st) => st.toUpperCase())}</>;
  if (MONEY_KEYS.includes(name)) return <>{formatMoney(Number(value))}</>;
  // Counts get thousands separators; ids and years stay as written.
  if (typeof value === "number" && Number.isInteger(value) && !/(^id$|_id$|year)/.test(name))
    return <>{value.toLocaleString()}</>;
  if (/(_rate|completion_pct)$/.test(name) && Number.isFinite(Number(value)))
    return <>{Number(value)}%</>;
  return <Value value={value} />;
}
export function Value({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === "") return <>—</>;
  if (Array.isArray(value))
    return value.length &&
      value.every((v) => typeof v === "object" && v !== null) ? (
      <DataTable rows={value} />
    ) : value.length > 1 && value.every((v) => typeof v === "string") && value.some((v) => (v as string).length > 40) ? (
      // Sentences (caveats, notes) read as a list, not one run-on line.
      <ul className="value-list">{(value as string[]).map((v, i) => <li key={i}>{v.replace(/_/g, " ")}</li>)}</ul>
    ) : (
      <>
        {value
          .map((v) => (typeof v === "object" ? JSON.stringify(v) : String(v)))
          .join(", ") || "—"}
      </>
    );
  if (typeof value === "object")
    return (
      <dl className="record-fields">
        {Object.entries(value as RecordData).map(([k, v]) => (
          <div key={k}>
            <dt>{label(k)}</dt>
            <dd>
              <Value value={v} />
            </dd>
          </div>
        ))}
      </dl>
    );
  return (
    <>{typeof value === "boolean" ? (value ? "Yes" : "No") : String(value)}</>
  );
}
/**
 * Shows a long list a page at a time: the first `step` items, then "Show N more".
 * Returns the visible slice and the control to render under it (null when the
 * list is short). `resetKey` changing (a new filter, a new client) starts over.
 */
export function useShowMore<T>(rows: T[], step = 25, resetKey: unknown = null) {
  const [state, setState] = useState({ limit: step, key: resetKey });
  const limit = state.key === resetKey ? state.limit : step;
  if (state.key !== resetKey) setState({ limit: step, key: resetKey });
  const visible = rows.slice(0, limit);
  const rest = rows.length - visible.length;
  const control = rows.length > step ? (
    <div className="show-more">
      <span className="muted">Showing {visible.length.toLocaleString()} of {rows.length.toLocaleString()}</span>
      {rest > 0 ? (
        <button type="button" className="button secondary" onClick={() => setState({ limit: limit + step, key: resetKey })}>
          Show {Math.min(step, rest).toLocaleString()} more
        </button>
      ) : (
        <button type="button" className="button secondary" onClick={() => setState({ limit: step, key: resetKey })}>Show fewer</button>
      )}
    </div>
  ) : null;
  return { visible, control };
}

export function DataTable({
  rows,
  columns,
  action,
  pageSize,
  empty = "No records in this view.",
}: {
  rows: RecordData[];
  columns?: string[];
  action?: (row: RecordData) => ReactNode;
  /** Show this many rows, then "Show more". Unset shows every row. */
  pageSize?: number;
  /** What an empty table says: name what is missing and how it gets filled. */
  empty?: ReactNode;
}) {
  const paged = useShowMore(rows, pageSize ?? Number.POSITIVE_INFINITY, rows.length);
  if (!rows.length)
    return <p className="workflow-note">{empty}</p>;
  const keys =
    columns ||
    [...new Set(rows.flatMap((r) => Object.keys(r)))]
      .filter(
        (k) =>
          ![
            "raw_data",
            "password_hash",
            "token",
            "access_token",
            "refresh_token",
          ].includes(k),
      )
      .slice(0, 8);
  const numeric = new Set(keys.filter((k) => isNumericColumn(k, rows)));
  const cellClass = (k: string, index: number) =>
    [index === 0 && !numeric.has(k) ? "lead" : "", numeric.has(k) ? "num" : ""].filter(Boolean).join(" ") || undefined;
  return (
    <div className="table-wrap">
      {/* Three columns or fewer (a label and a number or two) still fit a phone as a real table. */}
      <table className={`workflow-table data-table${keys.length + (action ? 1 : 0) <= 3 ? " data-table-compact" : ""}`}>
        <thead>
          <tr>
            {keys.map((k) => (
              <th key={k} className={numeric.has(k) ? "num" : undefined}>{label(k)}</th>
            ))}
            {action && <th className="data-table-action-head"><span className="sr-only">Actions</span></th>}
          </tr>
        </thead>
        <tbody>
          {paged.visible.map((row, i) => (
            <tr key={String(row.id ?? row.call_id ?? i)}>
              {keys.map((k, index) => (
                <td key={k} data-label={label(k)} className={cellClass(k, index)} data-empty={row[k] === null || row[k] === undefined || row[k] === "" ? "" : undefined}>
                  <Cell name={k} value={row[k]} />
                </td>
              ))}
              {action && <td className="data-table-action">{action(row)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
      {paged.control}
    </div>
  );
}
export function RequestView({
  request,
  children,
}: {
  request: {
    loading: boolean;
    error: string | null;
    data: unknown;
    reload: () => void;
  };
  children: ReactNode;
}) {
  return request.loading ? (
    <LoadingState label="Loading workspace" />
  ) : request.error ? (
    <ErrorState message={request.error} onRetry={request.reload} />
  ) : (
    <>{children}</>
  );
}
export function Tabs({
  tabs,
  value,
  onChange,
  labels,
}: {
  tabs: string[];
  value: string;
  onChange: (v: string) => void;
  /** A tab's name on screen when its id does not read well on its own ("generate" → "Make an image"). */
  labels?: Record<string, string>;
}) {
  const strip = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  useEffect(() => {
    const el = strip.current;
    if (!el) return;
    const measure = () => setMore(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    // The active tab scrolls into the strip (never the page): on a phone "People" sat past the edge.
    const active = el.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (active && (active.offsetLeft < el.scrollLeft || active.offsetLeft + active.offsetWidth > el.scrollLeft + el.clientWidth))
      el.scrollLeft = active.offsetLeft - 16;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(el);
    return () => { el.removeEventListener("scroll", measure); observer?.disconnect(); };
  }, [value, tabs.length]);
  return (
    <div ref={strip} className="queue-statuses workflow-tabs" aria-label="Workspace views" data-more={more || undefined}>
      {tabs.map((t) => (
        <button
          className={`queue-status ${t === value ? "active" : ""}`}
          key={t}
          aria-pressed={t === value}
          onClick={() => onChange(t)}
        >
          {labels?.[t] ?? label(t)}
        </button>
      ))}
    </div>
  );
}
export function useMutation() {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const run = async <T,>(
    path: string,
    body: unknown,
    method = "POST",
    done?: (result: T) => void,
  ): Promise<T | undefined> => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await api<T>(path, {
        method,
        body: body instanceof FormData ? body : JSON.stringify(body),
      });
      done?.(result);
      return result;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save");
      return undefined;
    } finally {
      setBusy(false);
    }
  };
  return {
    busy,
    error,
    notice,
    setNotice,
    run,
    feedback: (
      <>
        {error && (
          <p className="inline-notice danger" role="alert">
            {error}
          </p>
        )}
        {notice && (
          <p className="inline-notice" role="status">
            {notice}
          </p>
        )}
      </>
    ),
  };
}
export function Details({ record }: { record: RecordData }) {
  return (
    <dl className="record-fields">
      {Object.entries(record)
        .filter(
          ([k, v]) =>
            v != null &&
            ![
              "id",
              "report_id",
              "success",
              "raw_data",
              "raw_payload",
              "token",
              "access_token",
              "refresh_token",
              "password_hash",
            ].includes(k),
        )
        .map(([k, v]) => (
          <div key={k}>
            <dt>{label(k)}</dt>
            <dd>
              {typeof v === "string" && v.length > 180 ? (
                <Markdown>{v}</Markdown>
              ) : (
                <Cell name={k} value={v} />
              )}
            </dd>
          </div>
        ))}
    </dl>
  );
}
export function NoClient() {
  return (
    <EmptyState
      title="Choose a client"
      description="Select the active client to open this workspace."
    />
  );
}
export type InputField = {
  name: string;
  label?: string;
  type?: "text" | "textarea" | "number" | "date" | "url" | "email";
  required?: boolean;
  options?: Array<string | { value: string; label: string }>;
  value?: unknown;
  min?: number;
  max?: number;
  step?: string;
  /** Example value shown inside the empty field, e.g. "(916) 555-0123". */
  placeholder?: string;
  /** One line under the field: what it is for or where to find the value. */
  hint?: string;
};
export function ActionForm({
  fields,
  onSubmit,
  submit = "Save",
  busy = false,
  busyLabel = "Saving…",
  onCancel,
}: {
  fields: InputField[];
  onSubmit: (data: RecordData) => Promise<void>;
  submit?: string;
  busy?: boolean;
  /** Button text while busy, e.g. "Generating…" for actions that save nothing. */
  busyLabel?: string;
  /** In a dialog: shows Cancel beside the submit button, like RecordForm. */
  onCancel?: () => void;
}) {
  return (
    <form
      className="operation-form"
      onSubmit={async (e) => {
        e.preventDefault();
        const raw = Object.fromEntries(new FormData(e.currentTarget));
        const data: RecordData = { ...raw };
        for (const f of fields) {
          if (f.type === "number")
            data[f.name] = raw[f.name] === "" ? null : Number(raw[f.name]);
        }
        await onSubmit(data);
      }}
    >
      {fields.map((f) => (
        <label key={f.name}>
          <span className="field-label">{f.label || label(f.name)}{f.required && <span className="field-required" aria-hidden="true"> *</span>}</span>
          {f.options ? (
            <select
              name={f.name}
              required={f.required}
              defaultValue={String(f.value ?? "")}
            >
              {f.options.map((o) => {
                const value = typeof o === "string" ? o : o.value;
                return (
                  <option key={value} value={value}>
                    {typeof o === "string" ? label(o) : o.label}
                  </option>
                );
              })}
            </select>
          ) : f.type === "textarea" ? (
            <textarea
              name={f.name}
              rows={4}
              required={f.required}
              defaultValue={String(f.value ?? "")}
              placeholder={f.placeholder}
            />
          ) : (
            <input
              name={f.name}
              type={f.type || "text"}
              required={f.required}
              defaultValue={String(f.value ?? "")}
              min={f.min}
              max={f.max}
              step={f.step}
              placeholder={f.placeholder}
            />
          )}
          {f.hint && <small className="field-hint">{f.hint}</small>}
        </label>
      ))}
      <div className="operation-actions">
        {onCancel && (
          <button type="button" className="button secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        )}
        <button className="button" disabled={busy}>
          {busy ? busyLabel : submit}
        </button>
      </div>
    </form>
  );
}
